import { MutableRefObject, useEffect, useState } from 'react';
import { Swiper as SwiperInstance } from 'swiper';

const useSwiperState = (
  swiperRef: MutableRefObject<SwiperInstance | undefined>,
): {
  isBeginning: boolean;
  isEnd: boolean;
  isOverflowing: boolean;
} => {
  const [isEnd, setIsEnd] = useState(true);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const swiper = swiperRef.current;

    const checkOverflow = () => {
      const ref = swiper?.el;

      if (ref) {
        setIsOverflowing(ref.scrollWidth > ref.clientWidth);
      }
    };

    if (!swiper) return;

    checkOverflow();

    window.addEventListener('resize', checkOverflow);
    swiper.on('update', checkOverflow);

    return () => {
      window.removeEventListener('resize', checkOverflow);
      swiper.off('update', checkOverflow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swiperRef.current]);

  useEffect(() => {
    const ref = swiperRef.current;

    if (!ref) return;

    const handleSlideChange = () => {
      // Check the progress to determine if we're at the edges
      // This updates immediately during the transition
      const progress = ref.progress;
      const isAtBeginning = progress <= 0;
      const isAtEnd = progress >= 1;

      setIsEnd(isAtEnd);
      setIsBeginning(isAtBeginning);
    };

    const handleReachEnd = () => {
      setIsEnd(true);
    };

    const handleReachBeginning = () => {
      setIsBeginning(true);
    };

    const handleProgress = () => {
      handleSlideChange();
    };

    handleSlideChange();

    ref.on('reachBeginning', handleReachBeginning);
    ref.on('reachEnd', handleReachEnd);
    ref.on('slideChange', handleSlideChange);
    ref.on('progress', handleProgress);
    ref.on('setTranslate', handleSlideChange);

    return () => {
      ref.off('reachBeginning', handleReachBeginning);
      ref.off('reachEnd', handleReachEnd);
      ref.off('slideChange', handleSlideChange);
      ref.off('progress', handleProgress);
      ref.off('setTranslate', handleSlideChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swiperRef.current]);

  return {
    isBeginning,
    isEnd,
    isOverflowing,
  };
};

export default useSwiperState;
